import { makeStyles } from '@material-ui/core/styles';

export default makeStyles((theme) => ({
  toolbar: theme.mixins.toolbar,

  kerst: {
    position: 'relative',
  },

  page: {
    position: 'relative',
  },

  /* Hero / intro block — one clearly bounded card */
  info: {
    position: 'relative',
    margin: '1.5rem auto 0',
    maxWidth: '960px',
    padding: '3rem 2.5rem 2.75rem',
    textAlign: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.72)',
    backdropFilter: 'blur(12px) saturate(140%)',
    WebkitBackdropFilter: 'blur(12px) saturate(140%)',
    borderRadius: '32px',
    border: '1px solid rgba(255, 255, 255, 0.55)',
    boxShadow: '0 24px 60px rgba(61, 30, 92, 0.14)',
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      padding: '2.25rem 1.25rem 2rem',
      margin: '1rem 0.5rem 0',
      borderRadius: '24px',
    },
    '& > h1': {
      fontFamily: 'Fraunces, serif',
      fontWeight: 700,
      fontSize: 'clamp(1.9rem, 4.2vw, 3.2rem)',
      lineHeight: 1.08,
      color: 'var(--color-plum)',
      maxWidth: '780px',
      margin: '0 auto 1rem',
      letterSpacing: '-0.015em',
      '& em, & i': {
        fontStyle: 'italic',
        color: 'var(--color-coral)',
      },
    },
    '& > p': {
      fontFamily: 'Inter, sans-serif',
      fontSize: '1.05rem',
      color: 'var(--color-text-soft)',
      maxWidth: '620px',
      margin: '0 auto 1.75rem',
      [theme.breakpoints.down('xs')]: {
        display: 'none',
      },
    },
    /* Visual divider before the shop-owner CTA */
    '& > span:not($heroBadge)': {
      display: 'inline-block',
      position: 'relative',
      fontFamily: 'Inter, sans-serif',
      fontSize: '0.9rem',
      color: 'var(--color-text-soft)',
      marginTop: '1rem',
      marginBottom: '0.85rem',
      paddingTop: '1.5rem',
      width: '100%',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        width: '80px',
        height: '1px',
        background: 'linear-gradient(90deg, transparent, rgba(61, 30, 92, 0.18), transparent)',
      },
    },
    '& > button > .MuiIconButton-label': {
      textAlign: 'center',
    },
  },

  heroBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.4rem',
    padding: '0.4rem 0.95rem',
    borderRadius: '999px',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    border: '1px solid rgba(255, 107, 107, 0.25)',
    color: 'var(--color-coral-deep)',
    fontFamily: 'Inter, sans-serif',
    fontSize: '0.78rem',
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    marginBottom: '1.25rem',
    boxShadow: '0 4px 14px rgba(232, 72, 79, 0.08)',
  },

  /* Category strip — horizontal scroll on mobile, centered grid on desktop */
  topbar: {
    padding: '1rem 0 2.5rem',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: '0.4rem',
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'nowrap',
      overflowX: 'auto',
      justifyContent: 'flex-start',
      padding: '1rem 0.5rem 1.75rem',
      scrollSnapType: 'x mandatory',
      WebkitOverflowScrolling: 'touch',
      '&::-webkit-scrollbar': { display: 'none' },
    },
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
  categoryDropdown: {
    display: 'none',
    [theme.breakpoints.down('xs')]: {
      display: 'block',
      padding: '1.5rem 0.75rem 0.75rem',
    },
  },
  categoryCard: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flexShrink: 0,
    minWidth: '110px',
    fontFamily: 'Inter, sans-serif',
    fontSize: '0.88rem',
    fontWeight: 500,
    color: 'var(--color-text-soft)',
    cursor: 'pointer',
    margin: '0.1rem 0.3rem',
    padding: '0.75rem 0.5rem 0.5rem',
    borderRadius: '20px',
    transition: 'transform .25s ease, color .2s ease',
    scrollSnapAlign: 'start',
    '&:hover': {
      transform: 'translateY(-4px)',
      color: 'var(--color-plum)',
      '& > div:first-child': {
        boxShadow:
          '0 0 0 4px rgba(255, 255, 255, 1)' +
          ', 0 0 0 6px rgba(255, 107, 107, 0.35)' +
          ', 0 16px 30px rgba(232, 72, 79, 0.18)',
      },
      '& svg': {
        transform: 'scale(1.08)',
      },
    },
  },
  categoryImgBox: {
    position: 'relative',
    width: '92px',
    height: '92px',
    borderRadius: '50%',
    backgroundColor: 'var(--color-white)',
    boxShadow: '0 6px 18px rgba(61, 30, 92, 0.10)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all .25s ease',
    [theme.breakpoints.down('xs')]: {
      width: '80px',
      height: '80px',
    },
  },
  categoryIcon: {
    fontSize: '2.15rem',
    color: 'var(--color-plum)',
    opacity: 0.9,
    transition: 'transform .35s ease, opacity .2s ease, color .2s ease',
    [theme.breakpoints.down('xs')]: {
      fontSize: '1.95rem',
    },
  },
  categoryText: {
    marginTop: '0.65rem',
    fontSize: '0.85rem',
    letterSpacing: '0.01em',
  },
  active: {
    color: 'var(--color-coral-deep)',
    fontWeight: 700,
    '& > div:first-child': {
      boxShadow:
        '0 0 0 4px rgba(255, 255, 255, 1)' +
        ', 0 0 0 6px var(--color-coral)' +
        ', 0 18px 32px rgba(232, 72, 79, 0.32)',
      transform: 'translateY(-2px)',
    },
    '& svg': {
      transform: 'scale(1.06)',
    },
    /* coral indicator dot below the label */
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: '-4px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: '24px',
      height: '3px',
      borderRadius: '999px',
      background: 'var(--color-coral)',
      boxShadow: '0 2px 6px rgba(232, 72, 79, 0.4)',
    },
  },

  /* Main content card */
  content: {
    padding: '2.25rem 1.5rem',
    backgroundColor: 'var(--color-background-white)',
    borderRadius: '32px',
    boxShadow: '0 18px 60px rgba(61, 30, 92, 0.08)',
    border: '1px solid rgba(61, 30, 92, 0.04)',
    [theme.breakpoints.down('sm')]: {
      padding: '1.5rem 1rem',
    },
  },

  /* Filter chip row */
  filters: {
    paddingBottom: '1.5rem',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '0.6rem',
    '& > *': {
      marginTop: '0.25rem',
    },
  },

  items: {
    width: '100%',
  },
  grid: {
    marginTop: '0.5rem',
  },

  /* "Nodig winkel uit" footer */
  invite: {
    marginTop: '2rem',
    textAlign: 'center',
    padding: '1.5rem 1rem',
    background: 'var(--gradient-soft)',
    borderRadius: '24px',
    border: '1px dashed var(--color-coral-soft)',
  },
  iconButton: {
    backgroundColor: 'var(--color-coral)',
    color: 'var(--color-white) !important',
    fontFamily: 'Inter, sans-serif',
    fontWeight: 600,
    fontSize: '0.95rem',
    padding: '0.65rem 1.5rem',
    borderRadius: '999px',
    boxShadow: '0 8px 22px rgba(232, 72, 79, 0.28)',
    transition: 'transform .18s ease, box-shadow .18s ease, background-color .18s ease',
    '&:hover': {
      backgroundColor: 'var(--color-coral-deep)',
      transform: 'translateY(-1px)',
      boxShadow: '0 12px 28px rgba(232, 72, 79, 0.38)',
    },
  },
  iconButton2: {
    backgroundColor: 'transparent',
    color: 'var(--color-plum)',
    fontFamily: 'Inter, sans-serif',
    fontSize: '0.95rem',
    fontWeight: 500,
    padding: '0.5rem 1rem',
    borderRadius: '999px',
    '& u': {
      color: 'var(--color-coral)',
      textDecorationThickness: '2px',
      textUnderlineOffset: '3px',
    },
    '&:hover': {
      backgroundColor: 'rgba(255, 107, 107, 0.08)',
    },
  },

  /* OrganizationProducts page — header banner */
  header: {
    position: 'relative',
    paddingTop: '7rem',
    paddingBottom: '3rem',
    background:
      'radial-gradient(circle at 15% 30%, color-mix(in srgb, var(--color-coral) 45%, transparent), transparent 55%),' +
      'radial-gradient(circle at 85% 20%, rgba(244, 201, 93, 0.35), transparent 50%),' +
      'linear-gradient(180deg, color-mix(in srgb, var(--color-coral) 25%, var(--color-cream)) 0%, var(--color-cream) 100%)',
    [theme.breakpoints.down('sm')]: {
      paddingTop: '5.5rem',
      paddingBottom: '2rem',
    },
  },
  root: {
    flexGrow: 1,
  },
  topbar2: {
    backgroundSize: 'cover',
    backgroundPosition: 'bottom',
    backgroundRepeat: 'no-repeat',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '95%',
    maxWidth: '1100px',
    margin: 'auto',
  },
  root2: {
    padding: '2rem 2.25rem',
    borderRadius: '32px',
    margin: '0',
    width: '100%',
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
    backdropFilter: 'blur(10px) saturate(140%)',
    WebkitBackdropFilter: 'blur(10px) saturate(140%)',
    border: '1px solid rgba(255, 255, 255, 0.6)',
    boxShadow: '0 24px 60px rgba(61, 30, 92, 0.12)',
    color: 'var(--color-text)',
    [theme.breakpoints.down('sm')]: {
      padding: '1.5rem 1.25rem',
      borderRadius: '24px',
    },
  },
  cardBody: {
    display: 'flex',
    alignItems: 'center',
    gap: '2rem',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: '1rem',
      textAlign: 'center',
    },
    '& h1': {
      fontFamily: 'Fraunces, serif',
      fontWeight: 700,
      fontSize: 'clamp(1.7rem, 3.5vw, 2.5rem)',
      color: 'var(--color-plum)',
      letterSpacing: '-0.01em',
      marginBottom: '0.5rem',
    },
    '& button': {
      fontFamily: 'Inter, sans-serif',
      fontSize: '0.9rem',
      color: 'var(--color-text-soft)',
      padding: '0.25rem 0',
      '& svg': {
        fontSize: '1.05rem',
        color: 'var(--color-coral)',
        marginRight: '0.5rem',
      },
      '&:hover': {
        backgroundColor: 'transparent',
        color: 'var(--color-plum)',
      },
    },
  },
  content2: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    overflowWrap: 'anywhere',
    padding: '0 !important',
    [theme.breakpoints.down('sm')]: {
      alignItems: 'center',
    },
  },
  cover2: {
    width: 140,
    height: 140,
    borderRadius: '50%',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    flexShrink: 0,
    border: '4px solid #fff',
    boxShadow: '0 12px 30px color-mix(in srgb, var(--color-coral-deep) 30%, transparent)',
    [theme.breakpoints.down('sm')]: {
      width: 110,
      height: 110,
    },
  },
  cardFooter: {
    marginTop: '1.5rem',
    paddingTop: '1.5rem',
    borderTop: '1px solid rgba(61, 30, 92, 0.08)',
    '& p': {
      fontFamily: 'Inter, sans-serif',
      fontSize: '1rem',
      lineHeight: 1.65,
      color: 'var(--color-text-soft)',
      margin: 0,
    },
  },

  /* Products grid container on OrganizationProducts */
  products: {
    width: '100%',
  },
  productGrid: {
    marginTop: '1rem',
  },
}));
